import React, { useState, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Spinner,
  Label,
  Input
} from "reactstrap";
import { getServiceMonthlyReport, exportServiceMonthlyReportToExcel } from "../../components/Api/GmsBilling";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import Select from "react-select";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

// ─── Session helper ───────────────────────────────────────────────────────────
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const normalizeId = (v) => {
    if (v === null || v === undefined || v === "" || v === "null") return null;
    const n = typeof v === "string" ? parseInt(v, 10) : v;
    return isNaN(n) || n <= 0 ? null : n;
  };
  return {
    businessId: normalizeId(authUser.companyId || user.user_Company_Id),
    branchId: normalizeId(authUser.branchId || user.user_Branch_Id),
  };
};

const selectStyles = {
  control: (p, s) => ({
    ...p,
    minHeight: "38px",
    borderColor: s.isFocused ? "#556ee6" : "#ced4da",
    boxShadow: s.isFocused ? "0 0 0 0.2rem rgba(85,110,230,0.25)" : "none",
  }),
  menu: (p) => ({ ...p, zIndex: 9999, maxHeight: 200 }),
  menuList: (p) => ({ ...p, maxHeight: 180, overflowY: "auto" }),
};

// Helper function to generate all months between two dates
const getMonthsInRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const months = [];

  const current = new Date(start.getFullYear(), start.getMonth(), 1);
  while (current <= end) {
    const monthName = current.toLocaleString('default', { month: 'long' });
    const year = current.getFullYear();
    months.push({
      key: `${monthName}-${year}`, // used for matching data
      label: `${monthName} ${year}`, // used for display
      monthNum: current.getMonth() + 1,
      year: year
    });
    current.setMonth(current.getMonth() + 1);
  }
  return months;
};

const ServiceMonthlyReportPage = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reportData, setReportData] = useState([]);
  
  // Default range: Beginning of current year to current month
  const today = new Date();
  const defaultFrom = `${today.getFullYear()}-01`;
  const defaultTo = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const [monthColumns, setMonthColumns] = useState([]);

  const session = useMemo(() => getUserSession(), []);
  
  // Dropdown options
  const [businessOptions, setBusinessOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  
  // Selected values
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // ── Load businesses on mount, auto-bind from session ─────────────────────
  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await getBusinesses({ business_Active: true });
        const data = Array.isArray(res)
          ? res
          : res?.Businesses || res?.businesses || res?.data || [];
        const opts = data.map((b) => ({
          value: b.Business_Id || b.business_Id,
          label: b.Business_NameEn || b.business_NameEn,
        }));
        setBusinessOptions(opts);
        if (session.businessId) {
          const match = opts.find((o) => o.value === Number(session.businessId));
          if (match) setSelectedBusiness(match);
        }
      } catch { /* silent */ }
    };
    load();
  }, [session.businessId]);

  // ── Load branches whenever selected business changes ──────────────────────
  React.useEffect(() => {
    const load = async () => {
      setBranchOptions([]);
      setSelectedBranch(null);
      if (!selectedBusiness) return;
      try {
        const res = await getBranches({
          Branch_Buisness_Id: selectedBusiness.value,
          branch_Active: true,
        });
        const data = Array.isArray(res)
          ? res
          : res?.Branches || res?.branches || res?.data || [];
        const opts = data.map((b) => ({
          value: b.Branch_Id || b.branch_Id,
          label: b.Branch_NameEn || b.branch_NameEn,
        }));
        setBranchOptions(opts);
        if (session.branchId) {
          const match = opts.find((o) => o.value === Number(session.branchId));
          if (match) setSelectedBranch(match);
        }
      } catch { /* silent */ }
    };
    load();
  }, [selectedBusiness, session.branchId]);

  const fetchReport = async () => {
    if (!selectedBusiness || !selectedBranch) {
      Swal.fire("Validation", "Please select both Company and Branch before searching.", "warning");
      return;
    }
    if (!fromDate || !toDate) {
      toast.warning("Please select both From and To months.");
      return;
    }

    // Convert fromDate (YYYY-MM) to start of month
    const start = new Date(`${fromDate}-01T00:00:00Z`);
    // Convert toDate (YYYY-MM) to end of month
    const [toYear, toMonth] = toDate.split('-');
    const end = new Date(toYear, toMonth, 0, 23, 59, 59); // Last day of month

    if (start > end) {
      toast.warning("From Month cannot be later than To Month.");
      return;
    }

    setLoading(true);
    try {
      const response = await getServiceMonthlyReport({
        fromDate: start.toISOString(),
        toDate: end.toISOString(),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value
      });

      if (response && response.isSuccess) {
        setReportData(response.data || []);
        // Generate the dynamic columns based on the selected range
        setMonthColumns(getMonthsInRange(start, end));
      } else {
        toast.error(response?.message || "Failed to fetch report data.");
        setReportData([]);
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("An error occurred while fetching the report.");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Process data for the pivot table
  const pivotedData = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];

    const groupedMap = new Map();

    reportData.forEach(item => {
      // Create a unique key for each item
      const key = `${item.itemName}-${item.itemCategory}`;
      
      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          itemName: item.itemName,
          itemCategory: item.itemCategory,
          ...monthColumns.reduce((acc, col) => ({ ...acc, [col.key]: 0 }), {}) // initialize all months with 0
        });
      }

      const row = groupedMap.get(key);
      const monthKey = `${item.salesMonth}-${item.salesYear}`;
      
      // If the data falls within our generated columns, add the amount
      if (row[monthKey] !== undefined) {
        row[monthKey] += (item.totalAmountGenerated || 0);
      }
    });

    return Array.from(groupedMap.values());
  }, [reportData, monthColumns]);

  // Calculate Grand Totals
  const grandTotals = useMemo(() => {
    const totals = {};
    monthColumns.forEach(col => {
      totals[col.key] = pivotedData.reduce((sum, row) => sum + (row[col.key] || 0), 0);
    });
    return totals;
  }, [pivotedData, monthColumns]);

  // Export Excel Data
  const handleExportExcel = async () => {
    if (!fromDate || !toDate || !selectedBusiness || !selectedBranch) {
      toast.warning("Please make sure all filters are selected.");
      return;
    }

    const start = new Date(`${fromDate}-01T00:00:00Z`);
    const [toYear, toMonth] = toDate.split('-');
    const end = new Date(toYear, toMonth, 0, 23, 59, 59);

    setExporting(true);
    try {
      await exportServiceMonthlyReportToExcel({
        fromDate: start.toISOString(),
        toDate: end.toISOString(),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value
      });
      toast.success("Export successful");
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("An error occurred during export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Reports" breadcrumbItem="Service Monthly Report" />

          <Card>
            <CardBody>
              <Row className="mb-4 align-items-end g-2">
                <Col md={3}>
                  <Label className="fw-semibold mb-1" style={{ fontSize: "0.82rem" }}>
                    Company <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={businessOptions}
                    value={selectedBusiness}
                    onChange={(opt) => {
                      setSelectedBusiness(opt);
                      setSelectedBranch(null);
                    }}
                    styles={selectStyles}
                    placeholder="Select Company"
                    isClearable={false}
                  />
                </Col>
                <Col md={3}>
                  <Label className="fw-semibold mb-1" style={{ fontSize: "0.82rem" }}>
                    Branch <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={branchOptions}
                    value={selectedBranch}
                    onChange={setSelectedBranch}
                    styles={selectStyles}
                    placeholder={selectedBusiness ? "Select Branch" : "Select Company first"}
                    isClearable={false}
                    isDisabled={!selectedBusiness}
                  />
                </Col>
                <Col md={2}>
                  <Label className="fw-semibold mb-1" style={{ fontSize: "0.82rem" }}>From Month</Label>
                  <Input 
                    type="month" 
                    value={fromDate} 
                    onChange={e => setFromDate(e.target.value)} 
                  />
                </Col>
                <Col md={2}>
                  <Label className="fw-semibold mb-1" style={{ fontSize: "0.82rem" }}>To Month</Label>
                  <Input 
                    type="month" 
                    value={toDate} 
                    onChange={e => setToDate(e.target.value)} 
                  />
                </Col>
                <Col md={2}>
                  <div className="d-flex gap-2 w-100">
                    <Button color="primary" onClick={fetchReport} disabled={loading} className="flex-grow-1 text-nowrap">
                      {loading ? <Spinner size="sm" /> : "Search"}
                    </Button>
                    
                    {pivotedData.length > 0 && (
                      <Button 
                        color="success"
                        onClick={handleExportExcel}
                        disabled={exporting}
                        className="text-nowrap"
                      >
                        {exporting ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-export me-1"></i>}
                        Export Excel
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Data Table */}
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table className="table table-bordered table-hover table-striped mb-0 text-nowrap align-middle" style={{ fontSize: "0.82rem" }}>
                  <thead style={{ backgroundColor: "#f0f3ff" }}>
                    <tr>
                      <th style={{ minWidth: '150px' }}>Category</th>
                      <th style={{ minWidth: '200px' }}>Item Name</th>
                      {monthColumns.map(col => (
                        <th key={col.key} className="text-end" style={{ minWidth: '120px' }}>
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={monthColumns.length + 2} className="text-center py-4">
                          <Spinner color="primary" />
                          <div className="mt-2">Fetching data...</div>
                        </td>
                      </tr>
                    ) : pivotedData.length > 0 ? (
                      pivotedData.map((row, index) => (
                        <tr key={index}>
                          <td>{row.itemCategory}</td>
                          <td>{row.itemName}</td>
                          {monthColumns.map(col => (
                            <td key={col.key} className="text-end">
                              {row[col.key] > 0 ? row[col.key].toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={monthColumns.length + 2} className="text-center py-4">
                          No data found for the selected period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {pivotedData.length > 0 && (
                    <tfoot style={{ backgroundColor: "#f0f3ff", fontWeight: 'bold' }}>
                      <tr>
                        <td colSpan="2" className="text-end">Grand Total:</td>
                        {monthColumns.map(col => (
                          <td key={`total-${col.key}`} className="text-end text-primary">
                            {grandTotals[col.key].toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              
            </CardBody>
          </Card>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ServiceMonthlyReportPage;
